/**
 * @fileoverview API client service for KaraoNow frontend.
 * @summary Centralized API client with JWT interceptors and error handling.
 *
 * @package KaraoNow
 * @module apps/front/src/services/api
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { API_BASE_URL } from '../config/api.js'

/**
 * @summary Standard API response format.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export interface ApiResponse<T = any> {
  success: boolean
  message?: string
  data?: T
  code?: string
  errors?: any[]
  error?: string
}

/**
 * @summary Create and configure Axios instance.
 * @returns {AxiosInstance} Configured Axios instance.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Important for httpOnly cookies (JWT tokens)
    responseType: 'json', // Explicitly set response type to JSON
    timeout: 30000, // 30 seconds timeout (same as backend socket timeout)
  })

  // Request interceptor: Add JWT token from localStorage if available (fallback for API clients)
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Try to get token from localStorage (fallback for API clients that don't use cookies)
      const token = localStorage.getItem('accessToken')
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }

      // Add Accept-Language header from localStorage or default
      const language = localStorage.getItem('i18nextLng') || 'en'
      if (config.headers) {
        config.headers['Accept-Language'] = language
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    },
  )

  // Response interceptor: Handle errors and token refresh
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      return response
    },
    async (error) => {
      const originalRequest = error.config

      // Don't try to refresh token for auth-related endpoints
      // /auth/me is used to check authentication status, so 401 is expected when not authenticated
      // /auth/refresh is the refresh endpoint itself
      // /auth/login, /auth/register, /auth/set-password are authentication endpoints where 401 is expected
      const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') || 
                             originalRequest?.url?.includes('/auth/register') ||
                             originalRequest?.url?.includes('/auth/set-password') ||
                             originalRequest?.url?.includes('/auth/me') ||
                             originalRequest?.url?.includes('/auth/refresh')

      // Handle 401 Unauthorized (token expired) - but not for auth endpoints
      if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        originalRequest._retry = true

        try {
          // Try to refresh token
          const refreshResponse = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            {},
            { withCredentials: true },
          )

          if (refreshResponse.data.success) {
            // Retry original request
            return client(originalRequest)
          }
        } catch (refreshError) {
          // Refresh failed, don't redirect (let the app handle it gracefully)
          localStorage.removeItem('accessToken')
          return Promise.reject(refreshError)
        }
      }

      return Promise.reject(error)
    },
  )

  return client
}

/**
 * @summary Default API client instance.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const apiClient = createApiClient()

/**
 * @summary Type-safe API request helper.
 * @param {AxiosRequestConfig} config - Axios request configuration.
 * @returns {Promise<ApiResponse<T>>} API response.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export async function apiRequest<T = any>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
  try {
    const response = await apiClient.request<ApiResponse<T>>(config)
    return response.data
  } catch (error: any) {
    // Extract error message from response
    // Fastify sends JSON responses, so error.response.data should contain the response body
    if (error.response) {
      // Check if data is an empty object (common issue with Axios and Fastify)
      const data = error.response.data
      
      if (data && typeof data === 'object') {
        const keys = Object.keys(data)
        
        // If data is empty object, the body might not have been parsed
        // Try to manually parse the response if available
        if (keys.length === 0) {
          // Check if we can access the raw response text from the request
          const request = (error.response as any).request
          if (request && request.responseText) {
            try {
              const parsed = JSON.parse(request.responseText)
              return parsed as ApiResponse<T>
            } catch (e) {
              // Failed to parse, continue
            }
          }
          // Try other possible locations
          const responseText = (error.response as any).text || (error.response as any).body || (error.response as any).data
          if (responseText && typeof responseText === 'string' && responseText.length > 0) {
            try {
              const parsed = JSON.parse(responseText)
              return parsed as ApiResponse<T>
            } catch (e) {
              // Failed to parse, continue
            }
          }
        } else {
          // Data has keys, use it
          return data as ApiResponse<T>
        }
      }
      // If data is a string, try to parse it
      if (data && typeof data === 'string') {
        try {
          const parsed = JSON.parse(data)
          return parsed as ApiResponse<T>
        } catch (e) {
          // Failed to parse, continue
        }
      }
    }
    return {
      success: false,
      message: error.message || 'An error occurred',
      code: 'NETWORK_ERROR',
    }
  }
}

/**
 * @summary Create a Stripe checkout session.
 * @description Calls the backend endpoint to create a Stripe checkout session.
 *
 * @param {string} planType - The subscription plan type (one_shot, premium).
 * @returns {Promise<ApiResponse<{sessionId: string, url: string, clientSecret: string}>>} Checkout session data.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export async function createCheckoutSession(planType: string): Promise<
  ApiResponse<{
    sessionId?: string
    url?: string
    clientSecret?: string | null
    message?: string
    planType?: string
    updated?: boolean
  }>
> {
  try {
    console.log('🔄 Frontend: Calling /payments/checkout with planType:', planType)
    const response = await apiClient.post('/payments/checkout', {
      planType,
    })
    console.log('✅ Frontend: Received response:', response)
    console.log('   Response status:', response.status)
    console.log('   Response data:', response.data)
    console.log('   Response headers:', response.headers)
    return response.data as ApiResponse
  } catch (error: any) {
    console.error('❌ Frontend: Checkout error:', error)
    console.error('   Error response:', error.response)
    console.error('   Error response data:', error.response?.data)
    console.error('   Error response status:', error.response?.status)
    return {
      success: false,
      message: error.message || 'Checkout failed',
      code: 'CHECKOUT_ERROR',
    }
  }
}

/**
 * @summary Create a Stripe checkout session for a ONE_SHOT event upgrade.
 * @description Calls the backend endpoint to create a Stripe checkout session for upgrading a specific event to ONE_SHOT plan.
 *
 * @param {string} eventId - The event ID to upgrade.
 * @returns {Promise<ApiResponse<{sessionId: string, url: string, clientSecret: string}>>} Checkout session data.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export async function createCheckoutSessionForEvent(
  eventIdOrData: string | { name: string; description?: string; date: string; venueId?: string }
): Promise<
  ApiResponse<{
    sessionId: string
    url: string
    clientSecret: string | null
  }>
> {
  try {
    const body = typeof eventIdOrData === 'string'
      ? { eventId: eventIdOrData } // Conversion: existing event
      : { eventData: eventIdOrData } // Creation: new event data

    const response = await apiClient.post('/payments/checkout-event', body)
    return response.data as ApiResponse
  } catch (error: any) {
    return {
      success: false,
      message: error.message || 'Checkout failed for event upgrade',
      code: 'CHECKOUT_EVENT_ERROR',
    }
  }
}

/**
 * @summary Cancel subscription and schedule downgrade to FREE plan.
 * @description Cancels the user's subscription at the end of the current billing period.
 * User keeps access to their current plan until the period ends, then automatically downgrades to FREE.
 * @returns {Promise<ApiResponse>} Response with subscription details and cancellation date.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export async function cancelSubscription(): Promise<
  ApiResponse<{
    subscription: {
      type: string
      status: string
      canceledAt: string
      cancelAtPeriodEnd: boolean
      currentPeriodEnd: string
    }
  }>
> {
  try {
    const response = await apiClient.post('/subscription/cancel')
    return response.data as ApiResponse
  } catch (error: any) {
    return {
      success: false,
      message: error.message || 'Failed to cancel subscription',
      code: 'CANCEL_SUBSCRIPTION_ERROR',
    }
  }
}

/**
 * @summary Check quota for event date change.
 * @description Checks if changing an event's date would exceed the monthly quota.
 * Used for frontend validation (onBlur on date field).
 *
 * @param {string} eventId - The event ID.
 * @param {string} date - The new date in ISO format.
 * @returns {Promise<ApiResponse<{allowed: boolean, currentCount?: number, limit?: number, reason?: string}>>} Quota check result.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export async function checkEventQuotaForDate(eventId: string, date: string): Promise<
  ApiResponse<{
    allowed: boolean
    currentCount?: number
    limit?: number
    reason?: string
  }>
> {
  try {
    const response = await apiClient.post(`/events/${eventId}/check-quota`, {
      date,
    })
    return response.data as ApiResponse
  } catch (error: any) {
    // Extract error from response if available
    if (error.response?.data) {
      return error.response.data as ApiResponse
    }
    return {
      success: false,
      message: error.message || 'Failed to check quota',
      code: 'CHECK_QUOTA_ERROR',
    }
  }
}

/**
 * @summary Check quota for event creation.
 * @description Checks if creating an event with a specific date would exceed the monthly quota.
 * Used for frontend validation (onBlur on date field during creation).
 *
 * @param {string} date - The event date in ISO format.
 * @returns {Promise<ApiResponse<{allowed: boolean, currentCount?: number, limit?: number, reason?: string}>>} Quota check result.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export async function checkEventQuotaForCreation(date: string): Promise<
  ApiResponse<{
    allowed: boolean
    currentCount?: number
    limit?: number
    reason?: string
  }>
> {
  try {
    const { API_ENDPOINTS } = await import('../config/api.js')
    const response = await apiClient.post(API_ENDPOINTS.EVENTS.CHECK_QUOTA_CREATE, {
      date,
    })
    return response.data as ApiResponse
  } catch (error: any) {
    // Extract error from response if available
    if (error.response?.data) {
      return error.response.data as ApiResponse
    }
    return {
      success: false,
      message: error.message || 'Failed to check quota',
      code: 'CHECK_QUOTA_ERROR',
    }
  }
}